const CREATE_ID = () => (
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const RESERVED_IDS = new Set([
  "bulk", "deleted", "lineage-options", "manual", "mating-units", "me",
  "queen-cells", "workspace",
]);

function parseRequest(config = {}) {
  const url = new URL(String(config.url || ""), "https://offline.bijen.invalid/api/");
  const segments = url.pathname.split("/").filter(Boolean);
  if (segments[0] === "api") segments.shift();
  let data = config.data || {};
  if (typeof data === "string") {
    try { data = JSON.parse(data); } catch { data = {}; }
  }
  return {
    method: String(config.method || "get").toLowerCase(),
    segments,
    query: url.searchParams,
    data: data && typeof data === "object" ? data : {},
  };
}

function read(entityType, options = {}) {
  return { mode: "read", entityType, responseKind: "collection", ...options };
}

function command(actionType, entityType, request, options = {}) {
  const routeId = options.entityId ?? request.segments[1] ?? request.data.id ?? null;
  const entityId = routeId && !RESERVED_IDS.has(routeId) ? routeId : `local-${CREATE_ID()}`;
  return {
    mode: "command",
    actionType,
    entityType,
    entityId,
    operation: request.method === "delete" ? "delete" : "upsert",
    data: request.data,
    ...options,
  };
}

export function describeOfflineRequest(config = {}) {
  const request = parseRequest(config);
  const [resource, second, third] = request.segments;
  const id = second && !RESERVED_IDS.has(second) ? second : null;

  if (resource === "mobile" && second === "location" && request.method === "post") {
    const hiveId = request.data.hive_id ?? request.data.hiveId;
    return hiveId
      ? command("CAPTURE_HIVE_LOCATION", "hives", request, { entityId: hiveId })
      : { mode: "unsupported", reason: "Kast-id ontbreekt voor locatie" };
  }
  if (resource === "mobile" && second === "scan-result" && request.method === "post") {
    const scanId = request.data.scan_id ?? request.data.scanId ?? request.data.clientActionId ?? request.data.client_action_id ?? CREATE_ID();
    return command("SCAN_RESULT_CAPTURED", "scan_events", request, { entityId: `local-scan-${scanId}` });
  }
  if (resource === "auth" && request.method !== "get") return { mode: "server" };
  if (["auth", "mobile"].includes(resource)) return { mode: "network" };

  if (request.method === "get") {
    if (resource === "dashboard" && !second) return read("dashboard", { responseKind: "singleton" });
    if (resource === "apiaries" && second === "deleted") return read("apiaries", { onlyDeleted: true });
    if (resource === "apiaries" && id && third === "stats") return read("apiaries", { entityId: id, responseKind: "apiaryStats" });
    if (resource === "apiaries" && id) return read("apiaries", { entityId: id, responseKind: "entity" });
    if (resource === "apiaries" && !second) return read("apiaries");

    if (resource === "hives" && second === "deleted") return read("hives", { onlyDeleted: true });
    if (resource === "hives" && id) return read("hives", { entityId: id, responseKind: "entity" });
    if (resource === "hives" && !second) return read("hives", { filters: { apiary_id: request.query.get("apiary_id") || undefined } });

    if (resource === "inspections" && !second) return read("inspections", { filters: { hive_id: request.query.get("hive_id") || undefined } });
    if (resource === "inspections" && id) return read("inspections", { entityId: id, responseKind: "entity" });
    if (resource === "tasks" && !second) return read("tasks", { filters: { status: request.query.get("status") || undefined } });
    if (resource === "suggestions" && !second) return read("suggestions", { filters: { status_filter: request.query.get("status_filter") || undefined } });
    if (resource === "weather" && second) return read("weather", { parentId: second, responseKind: "weather" });

    if (resource === "queens" && second === "lineage-options") return read("queens", { responseKind: "lineageOptions" });
    if (resource === "queens" && id && third === "history") return read("queen_history", { parentId: id });
    if (resource === "queens" && id && third === "recommendation") return { mode: "static", value: null };
    if (resource === "queens" && id) return read("queens", { entityId: id, responseKind: "entity" });
    if (resource === "queens" && !second) return read("queens");

    // The native workspace is derived by the page from the contract collections.
    // It is deliberately not a separate SQLite/bootstrap collection.
    if (resource === "breeding-batches" && second === "workspace") return { mode: "static", value: null };
    if (resource === "breeding-batches" && second === "mating-units") return read("mating_units", third ? { entityId: third, responseKind: "entity" } : {});
    if (resource === "breeding-batches" && second === "queen-cells") return read("queen_cells", third ? { entityId: third, responseKind: "entity" } : {});
    if (resource === "breeding-batches" && id && third === "queen-cells") return read("queen_cells", { parentId: id });
    if (resource === "breeding-batches" && id) return read("breeding_batches", { entityId: id, responseKind: "entity" });
    if (resource === "breeding-batches" && !second) return read("breeding_batches");

    if (resource === "calendar") return read("calendar_entries", {
      responseKind: "calendar",
      filters: { start: request.query.get("start") || undefined, end: request.query.get("end") || undefined },
    });
    if (resource === "orgs" && second === "me") return read("organization", { responseKind: "team" });
    if (resource === "export" && second?.endsWith(".csv")) return read("export", { responseKind: "export", exportType: second.slice(0, -4) });
    return { mode: "unsupported" };
  }

  if (resource === "apiaries" && second === "deleted") return { mode: "unsupported" };
  if (resource === "apiaries" && id && third === "restore") return command("RESTORE_APIARY", "apiaries", request, { entityId: id });
  if (resource === "apiaries" && request.method === "post" && !second) return command("CREATE_APIARY", "apiaries", request);
  if (resource === "apiaries" && id && ["put", "patch"].includes(request.method)) return command("UPDATE_APIARY", "apiaries", request, { entityId: id });
  if (resource === "apiaries" && id && request.method === "delete") return command("DELETE_APIARY", "apiaries", request, { entityId: id });

  if (resource === "hives" && id && third === "restore") return command("RESTORE_HIVE", "hives", request, { entityId: id });
  if (resource === "hives" && id && third === "move") return command("MOVE_HIVE", "hives", request, { entityId: id });
  if (resource === "hives" && id && third === "split") return command("SPLIT_HIVE", "hives", request, { entityId: id });
  if (resource === "hives" && id && third === "queen") return command("ASSIGN_HIVE_QUEEN", "hives", request, { entityId: id });
  if (resource === "hives" && id && third === "qr-code") return command("SAVE_QR_ACTION", "hives", request, { entityId: id });
  if (resource === "hives" && id && third === "status" && ["put", "patch"].includes(request.method)) return command("UPDATE_HIVE_STATUS", "hives", request, { entityId: id });
  if (resource === "hives" && request.method === "post" && !second) return command("CREATE_HIVE", "hives", request);
  if (resource === "hives" && id && ["put", "patch"].includes(request.method)) return command("UPDATE_HIVE", "hives", request, { entityId: id });
  if (resource === "hives" && id && request.method === "delete") return command("DELETE_HIVE", "hives", request, { entityId: id });

  if (resource === "inspections" && request.method === "post" && !second) return command("CREATE_INSPECTION", "inspections", request);
  if (resource === "inspections" && id && ["put", "patch"].includes(request.method)) return command("UPDATE_INSPECTION", "inspections", request, { entityId: id });
  if (resource === "inspections" && id && request.method === "delete") return command("DELETE_INSPECTION", "inspections", request, { entityId: id });
  if (resource === "tasks" && second === "bulk" && request.method === "post") return command("BULK_UPDATE_TASKS", "tasks", request, { entityId: `local-${CREATE_ID()}` });
  if (resource === "tasks" && request.method === "post" && !second) return command("CREATE_TASK", "tasks", request);
  if (resource === "tasks" && id && ["put", "patch"].includes(request.method)) return command("UPDATE_TASK", "tasks", request, { entityId: id });
  if (resource === "tasks" && id && request.method === "delete") return command("DELETE_TASK", "tasks", request, { entityId: id });

  if (resource === "suggestions" && id && third === "accept") return command("ACCEPT_SUGGESTION", "suggestions", request, { entityId: id });
  if (resource === "suggestions" && id && third === "dismiss") return command("DISMISS_SUGGESTION", "suggestions", request, { entityId: id });
  if (resource === "suggestions" && id && third === "reopen") return command("REOPEN_SUGGESTION", "suggestions", request, { entityId: id });

  if (resource === "queens" && request.method === "post" && !second) return command("CREATE_QUEEN", "queens", request);
  if (resource === "queens" && id && ["put", "patch"].includes(request.method)) return command("UPDATE_QUEEN", "queens", request, { entityId: id });
  if (resource === "queens" && id && request.method === "delete") return command("DELETE_QUEEN", "queens", request, { entityId: id });

  if (resource === "breeding-batches" && second === "queen-cells" && third && ["put", "patch"].includes(request.method)) return command("UPDATE_QUEEN_CELL", "queen_cells", request, { entityId: third });
  if (resource === "breeding-batches" && second === "mating-units" && request.method === "post" && !third) return command("CREATE_MATING_UNIT", "mating_units", request);
  if (resource === "breeding-batches" && second === "mating-units" && third && ["put", "patch"].includes(request.method)) return command("UPDATE_MATING_UNIT", "mating_units", request, { entityId: third });
  if (resource === "breeding-batches" && request.method === "post" && !second) return command("CREATE_BREEDING_BATCH", "breeding_batches", request);
  if (resource === "breeding-batches" && id && ["put", "patch"].includes(request.method)) return command("UPDATE_BREEDING_BATCH", "breeding_batches", request, { entityId: id });
  if (resource === "breeding-batches" && id && request.method === "delete") return command("DELETE_BREEDING_BATCH", "breeding_batches", request, { entityId: id });

  if (resource === "orgs" && second === "invite" && request.method === "post") return command("CREATE_INVITE", "invites", request, { entityId: `local-invite-${CREATE_ID()}` });
  if (resource === "orgs" && second === "invites" && third === "accept" && request.method === "post") return command("ACCEPT_INVITE", "invites", request, { entityId: `local-${CREATE_ID()}` });
  if (resource === "orgs" && second === "invites" && third && request.method === "delete") return command("DELETE_INVITE", "invites", request, { entityId: third });
  if (resource === "orgs" && second === "members" && third && ["put", "patch"].includes(request.method)) return command("UPDATE_MEMBER_ROLE", "members", request, { entityId: third });
  if (resource === "orgs" && second === "members" && third && request.method === "delete") return command("REMOVE_MEMBER", "members", request, { entityId: third });

  return { mode: "unsupported" };
}

export function isOfflineCapableRequest(config) {
  return ["read", "command", "static", "server"].includes(describeOfflineRequest(config).mode);
}
